import { Box } from '@mui/material';
import { GridContainer, GridItem } from 'components/atomic/atoms/grid';
import Image from 'next/image';
import type { ReactNode } from 'react';
import { effects } from 'design-system/tokens/effects';
import { bentoTokens } from '../bento-tokens';
import { renderSeeAllButton } from './shared-helpers';

interface FourStateProps {
  images: string[];
  getAlt: (index: number) => string;
  onImageClick?: (index: number) => void;
  blur?: boolean;
  badgesSlot?: ReactNode;
  seeAllSlot?: (total: number) => ReactNode;
  total: number;
}

/**
 * Bento Four State (N=4 Images)
 * 
 * Layout: Main (left, 8/12) + 2 squares (top right) + long (bottom right)
 * - Left: Main Hero Image (8/12 columns, full height)
 * - Right Top: 2 squares side-by-side (4/12 columns, 50% height)
 * - Right Bottom: Long image (4/12 columns, 50% height)
 */
export function BentoFourState({
  images,
  getAlt,
  onImageClick,
  blur,
  badgesSlot,
  seeAllSlot,
  total,
}: FourStateProps) {
  const mainImage = images[0];
  const square1 = images[1];
  const square2 = images[2];
  const longImage = images[3];

  return (
    <GridContainer
      data-testid="gallery-bento-four"
      columnSpacing={bentoTokens.gap}
      sx={{
        filter: blur ? effects.blur[100] : 'none',
      }}
    >
      {/* Left: Main Hero Image (8/12 columns) */}
      <GridItem xs={bentoTokens.gridColumns.mainImage}>
        <Box
          onClick={() => onImageClick?.(0)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && onImageClick?.(0)}
          sx={{
            position: 'relative',
            width: '100%',
            height: bentoTokens.height,
            borderRadius: bentoTokens.borderRadius.main,
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          <Image
            src={mainImage}
            alt={getAlt(0)}
            fill
            sizes={bentoTokens.imageSizes.mainImage}
            style={{ objectFit: 'cover' }}
            priority
          />
          {badgesSlot && (
            <Box
              sx={{
                position: 'absolute',
                top: bentoTokens.overlay.badges,
                left: bentoTokens.overlay.badges,
                zIndex: bentoTokens.zIndex.badges,
              }}
            >
              {badgesSlot}
            </Box>
          )}
        </Box>
      </GridItem>

      {/* Right: Column with 2 squares + long image (4/12 columns) */}
      <GridItem xs={bentoTokens.gridColumns.thumbnail * 2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: bentoTokens.gap,
            height: bentoTokens.height,
          }}
        >
          {/* Top Row: 2 Squares Side-by-Side */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: bentoTokens.gap,
              flex: 1,
            }}
          >
            {/* Square 1 (Image 2) */}
            <Box
              onClick={() => onImageClick?.(1)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && onImageClick?.(1)}
              sx={{
                position: 'relative',
                flex: 1,
                borderRadius: bentoTokens.borderRadius.thumbnail,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
            >
              <Image
                src={square1}
                alt={getAlt(1)}
                fill
                sizes={bentoTokens.imageSizes.thumbnail}
                style={{ objectFit: 'cover' }}
              />
            </Box>

            {/* Square 2 (Image 3) */}
            <Box
              onClick={() => onImageClick?.(2)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && onImageClick?.(2)}
              sx={{
                position: 'relative',
                flex: 1,
                borderRadius: bentoTokens.borderRadius.thumbnail,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
            >
              <Image
                src={square2}
                alt={getAlt(2)}
                fill
                sizes={bentoTokens.imageSizes.thumbnail}
                style={{ objectFit: 'cover' }}
              />
            </Box>
          </Box>

          {/* Bottom Row: Long Image (Image 4) */}
          <Box
            onClick={() => onImageClick?.(3)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onImageClick?.(3)}
            sx={{
              position: 'relative',
              flex: 1,
              borderRadius: bentoTokens.borderRadius.thumbnail,
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            <Image
              src={longImage}
              alt={getAlt(3)}
              fill
              sizes={bentoTokens.imageSizes.thumbnail}
              style={{ objectFit: 'cover' }}
            />
            {renderSeeAllButton(seeAllSlot, total)}
          </Box>
        </Box>
      </GridItem>
    </GridContainer>
  );
}
